import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus, LayoutGrid, List, Share2 } from 'lucide-react';
import { fetchFormsWithCounts } from '~/services/analytics';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { Badge } from '~/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { FormCard } from '~/components/FormCard';
import { ShareModal } from '~/components/ShareModal';
import { cn } from '~/lib/utils';
import { format } from 'date-fns';
import type { IFormWithCount } from '~/types';

type ViewMode = 'grid' | 'list';
type TabValue = 'all' | 'external';

function getStoredView(): ViewMode {
  try {
    return (localStorage.getItem('forms-view') as ViewMode) || 'grid';
  } catch {
    return 'grid';
  }
}

export const FormsList = () => {
  const [forms, setForms] = useState<IFormWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<TabValue>('all');
  const [view, setView] = useState<ViewMode>(getStoredView);
  const [shareFormId, setShareFormId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchFormsWithCounts();
        setForms(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch forms');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('forms-view', view);
    } catch {
      // ignore
    }
  }, [view]);

  const filtered = useMemo(() => {
    let result = forms;
    if (tab === 'external') {
      result = result.filter((f) => f.status === 'published');
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((f) => f.name.toLowerCase().includes(q));
    }
    return result;
  }, [forms, tab, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive bg-destructive/10 p-4">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-6 w-6 text-foreground" />
          <h1 className="text-2xl font-bold">Forms</h1>
        </div>
        <div className="flex items-center gap-3">
          <Input
            placeholder="Search forms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
          <Button asChild>
            <Link to="/forms/new">
              <Plus className="mr-2 h-4 w-4" />
              Create new form
            </Link>
          </Button>
        </div>
      </div>

      {/* Tabs and view toggle */}
      <div className="flex items-center justify-between">
        <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
          <TabsList>
            <TabsTrigger value="all">
              All Forms
              <Badge variant="secondary" className="ml-1.5 px-1.5 text-xs">
                {forms.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="external">
              External Forms
              <Badge variant="secondary" className="ml-1.5 px-1.5 text-xs">
                {forms.filter((f) => f.status === 'published').length}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className={cn('h-8 w-8 p-0', view === 'grid' && 'bg-accent')}
            onClick={() => setView('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn('h-8 w-8 p-0', view === 'list' && 'bg-accent')}
            onClick={() => setView('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No forms found</h3>
          <p className="text-muted-foreground mb-6">
            {search ? 'Try a different search term' : 'Create your first form to get started'}
          </p>
          {!search && (
            <Button asChild>
              <Link to="/forms/new">Create Form</Link>
            </Button>
          )}
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((form) => (
            <FormCard key={form.id} form={form} onShare={setShareFormId} />
          ))}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Responses</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((form) => (
                <TableRow key={form.id}>
                  <TableCell>
                    <Link to={`/forms/${form.id}`} className="font-medium hover:underline">
                      {form.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={cn(
                        form.status === 'published' && 'bg-green-100 text-green-800',
                        form.status === 'draft' && 'bg-yellow-100 text-yellow-800',
                        form.status === 'archived' && 'bg-gray-100 text-gray-800',
                      )}
                    >
                      {form.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{form.submissionCount}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(form.updated_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setShareFormId(form.id)}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {shareFormId && <ShareModal formId={shareFormId} onClose={() => setShareFormId(null)} />}
    </div>
  );
};
